<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { LightbulbIcon, CircleCheckIcon, CircleInfoIcon } from 'Common/components/elements/icons/Icons'
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
//Types
import { DefectTypes, DefectStatusTypes } from 'Modules/types/pages/imageReview/enums'
import type { ImageAnnotationType } from 'Modules/types/pages/imageReview/imageAnnotationType'
import type { ImageType } from 'Modules/types/pages/imageReview/imageType'
import { getDefectStrokeColor } from './helperFunctions'
//Components
import DiscrepancyCategoryKey from 'Modules/pages/imageReview/DiscrepancyCategoryKey.vue'
import CollapsibleTableRow from '@/common/components/elements/surfaces/CollapsibleTableRow.vue'
interface PropTypes {
  image: ImageType
  activeSections: Array<string>
}

const props = defineProps<PropTypes>()

const store = useImageReviewStore()
const {
  annotations: storedAnnotations,
  confirmedAnnotations,
  suggestionsAnnotations,
  groupedAnnotationsMap,
  groupedAnnotationsArray,
  groupedSuggestionsMap,
  groupedSuggestionsArray,
  stagedAnnotations,
} = storeToRefs(store.annotationsStore)

const { stageAnnotationByUuid, unstageAnnotationByUuid, hoverAnnotationByUuid, unhoverAnnotationByUuid } =
  store.annotationsStore

const mousedOverRow = ref<HTMLElement>()

const handleRowClick = (annotation: ImageAnnotationType | undefined): void => {
  annotation.isHovered = false
  const annotationIsStaged = stagedAnnotations.value.find(
    (_annotation) => _annotation.annotationUuid === annotation.annotationUuid
  )
  const uuid = annotation.annotationUuid
  if (annotationIsStaged) unstageAnnotationByUuid(uuid)
  else stageAnnotationByUuid(uuid)
}

const handleGroupRowClick = (annotationGroudId: number, isSuggestion = false): void => {
  const map = isSuggestion ? groupedSuggestionsMap.value : groupedAnnotationsMap.value
  const annotationsInGroup = map[annotationGroudId]

  if (!annotationsInGroup) return

  const allGroupAnnotationsStaged = annotationsInGroup.every((annotation) =>
    stagedAnnotations.value.some((_annotation) => _annotation.annotationUuid === annotation.annotationUuid)
  )
  if (!allGroupAnnotationsStaged) {
    annotationsInGroup.forEach((annotation) => {
      stageAnnotationByUuid(annotation.annotationUuid)
    })
  } else {
    annotationsInGroup.forEach((annotation) => {
      unstageAnnotationByUuid(annotation.annotationUuid)
    })
  }
}

const handleMouseEnterRow = (e: MouseEvent, annotation: ImageAnnotationType | undefined): void => {
  mousedOverRow.value = e.target as HTMLElement
  hoverAnnotationByUuid(annotation.annotationUuid)
}

const handleMouseLeaveRow = (e: MouseEvent, annotation: ImageAnnotationType | undefined): void => {
  if (mousedOverRow.value === e.target) mousedOverRow.value = null
  unhoverAnnotationByUuid(annotation.annotationUuid)
}

const handleMouseEnterGroupRow = (e: MouseEvent, groupId: number, isSuggestion = false): void => {
  mousedOverRow.value = e.target as HTMLElement
  const map = isSuggestion ? groupedSuggestionsMap.value : groupedAnnotationsMap.value
  const annotations = map[groupId]
  if (annotations) {
    annotations.forEach((annotation) => hoverAnnotationByUuid(annotation.annotationUuid))
  }
}

const handleMouseLeaveGroupRow = (e: MouseEvent, groupId: number, isSuggestion = false): void => {
  if (mousedOverRow.value === e.target) mousedOverRow.value = null
  const map = isSuggestion ? groupedSuggestionsMap.value : groupedAnnotationsMap.value
  const annotations = map[groupId]
  if (annotations) {
    annotations.forEach((annotation) => unhoverAnnotationByUuid(annotation.annotationUuid))
  }
}

const getRowClass = (annotation: ImageAnnotationType): string => {
  const defect_type = DefectTypes[annotation.defectType]
  const category_color = getDefectStrokeColor(defect_type)
  const annotationIsStaged = stagedAnnotations.value.find(
    (_annotation) => _annotation.annotationUuid === annotation.annotationUuid
  )

  return `border-l-8 border-solid border-[${category_color}] ${annotation.isHovered ? 'bg-orange-100' : ''} ${
    annotationIsStaged && !annotation.isHovered ? 'bg-orange-200' : ''
  }`
}

const getCollapsedHeaderClass = (annotation: ImageAnnotationType): string => {
  const defect_type = DefectTypes[annotation.defectType]
  const category_color = getDefectStrokeColor(defect_type)

  const map = annotation.isAnnotation ? groupedAnnotationsMap.value : groupedSuggestionsMap.value
  const annotationsInGroup = map[annotation.annotationGroup] || []

  const isAnyAnnotationHovered = annotationsInGroup.some((annotation) => annotation.isHovered)
  const annotationIsStaged = stagedAnnotations.value.find(
    (_annotation) => _annotation.annotationUuid === annotation.annotationUuid
  )
  const bgColor = isAnyAnnotationHovered ? 'bg-orange-100' : ''
  const hoverColor = annotationIsStaged && !annotation.isHovered ? 'bg-orange-200' : ''

  return `border-l-8 border-solid border-[${category_color}] ${bgColor} ${hoverColor}`
}

const getCollapsedRowClass = (annotation: ImageAnnotationType): string => {
  const defect_type = DefectTypes[annotation.defectType]
  const category_color = getDefectStrokeColor(defect_type)
  return `border-l-8 border-solid border-[${category_color}]`
}

const getNestedRowClass = (annotation: ImageAnnotationType): string => {
  const annotationIsStaged = stagedAnnotations.value.find(
    (_annotation) => _annotation.annotationUuid === annotation.annotationUuid
  )

  return `${annotation.isHovered ? 'bg-orange-100' : ''} ${
    annotationIsStaged && !annotation.isHovered ? 'bg-orange-200' : ''
  }`
}

const getCollapseItemClass = (name: string) => {
  if (props.activeSections.includes(name)) {
    return 'grow'
  }
  return ''
}

function scrollDefects(annotation: ImageAnnotationType) {
  let element
  if (!annotation.annotationGroup) {
    element = document.getElementById(annotation.annotationUuid)
  } else {
    element = document.getElementById(`G-${annotation?.annotationGroup}`)
  }
  if (element && element !== mousedOverRow.value) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

const formatConfidence = (val: number | undefined): string => {
  return val !== undefined && val !== null ? (Number(val) * 100).toFixed(2) + '%' : ''
}

const getGroupAverageConfidence = (group: ImageAnnotationType[]): string => {
  if (!group || group.length === 0) return ''

  // Sum up all confidence values (treating null/undefined as 0)
  const total = group.reduce((sum, annotation) => {
    return sum + (Number(annotation.confidence) || 0)
  }, 0)

  // Calculate average
  const average = total / group.length

  return formatConfidence(average)
}

watch(storedAnnotations, (): void => {
  const firstHoveredAnnotation = storedAnnotations.value.find((annotation) => annotation?.isHovered === true)
  if (firstHoveredAnnotation) scrollDefects(firstHoveredAnnotation)
})

onUnmounted(() => {
  mousedOverRow.value = null
})
</script>

<template>
  <el-collapse-item name="defects" class="relative shrink basis-1 min-h-12" :class="getCollapseItemClass('defects')">
    <template #title>
      <div class="px-4 h-full w-full flex items-center justify-between gap-8 text-lg">
        <span class="font-medium">Discrepancies ({{ confirmedAnnotations?.length }})</span>
        <DiscrepancyCategoryKey />
      </div>
    </template>
    <div class="relative h-full w-full defects-tables overflow-y-scroll">
      <template v-if="confirmedAnnotations.length === 0">
        <div class="h-full w-full pb-4 flex items-center justify-center text-slate-300 text-base font-medium">
          No confirmed discrepancies exist
        </div>
      </template>
      <template v-else>
        <table class="defects-table table-fixed w-full text-[14px] text-left">
          <thead>
            <tr class="bg-gray-200 border-l-8 border-l-gray-200">
              <th class="w-[1em]"></th>
              <th class="w-[5em]">ID</th>
              <th>TYPE</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="groupedAnnotationsArray.length === 0"> No defects. </template>
            <template v-for="(row, i) in groupedAnnotationsArray" v-else :key="i">
              <template v-if="Array.isArray(row)">
                <CollapsibleTableRow
                  :id="`G-${row[0].annotationGroup}`"
                  class="hover:cursor-pointer"
                  :class="getCollapsedHeaderClass(row[0])"
                  @click="handleGroupRowClick(row[0].annotationGroup, false)"
                  @mouseenter="
                    (e) => {
                      handleMouseEnterGroupRow(e, row[0].annotationGroup, false)
                    }
                  "
                  @mouseleave="
                    (e) => {
                      handleMouseLeaveGroupRow(e, row[0].annotationGroup, false)
                    }
                  "
                >
                  <template #main>
                    <td class="border-r border-y border-gray-300">G-{{ row[0].annotationGroup }}</td>
                    <td class="border border-gray-300">{{ DefectTypes[row[0].defectType] }}</td>
                    <td class="border border-gray-300">{{ DefectStatusTypes[row[0].defectStatus] }}</td>
                  </template>
                  <template #details>
                    <tr :class="getCollapsedRowClass(row[0])">
                      <td colspan="4" class="no-padding">
                        <table class="w-full text-[14px] text-left">
                          <tbody>
                            <template v-for="(_row, _i) in row" :key="_i">
                              <tr
                                :id="_row.annotationUuid"
                                :class="getNestedRowClass(_row)"
                                class="hover:bg-orange-100 hover:cursor-pointer border-l-8 border-solid"
                                @click.stop="handleRowClick(_row)"
                                @mouseenter="(e) => handleMouseEnterRow(e, _row)"
                                @mouseleave="(e) => handleMouseLeaveRow(e, _row)"
                              >
                                <td colspan="2" class="border border-gray-300 text-center">{{ _row.identifier }}</td>
                                <td class="border border-gray-300">{{ DefectTypes[_row.defectType] }}</td>
                                <td class="border border-gray-300">{{ DefectStatusTypes[_row.defectStatus] }}</td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </template>
                </CollapsibleTableRow>
              </template>
              <template v-else>
                <tr
                  :id="row.annotationUuid"
                  class="hover:bg-orange-100 hover:cursor-pointer border-l-8 border-solid"
                  :class="getRowClass(row)"
                  @click="handleRowClick(row)"
                  @mouseenter="(e) => handleMouseEnterRow(e, row)"
                  @mouseleave="(e) => handleMouseLeaveRow(e, row)"
                >
                  <td colspan="2" class="border border-gray-300 text-center">{{ row.identifier }}</td>
                  <td class="border border-gray-300">{{ DefectTypes[row.defectType] }}</td>
                  <td class="border border-gray-300">{{ DefectStatusTypes[row.defectStatus] }}</td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </template>
    </div>
  </el-collapse-item>
  <el-collapse-item
    name="suggestions"
    class="relative shrink basis-1 min-h-12"
    :class="getCollapseItemClass('suggestions')"
  >
    <template #title>
      <div class="px-4 h-full w-full flex items-center justify-between gap-8 text-lg">
        <span class="font-medium">Suggestions ({{ suggestionsAnnotations?.length }})</span>
        <template v-if="props.image?.isAddsReviewed">
          <el-tooltip content="Advanced Damage Detection Complete" placement="right">
            <CircleCheckIcon class="pr-2 text-green-500" />
          </el-tooltip>
        </template>
        <template v-else>
          <el-tooltip content="Advanced Damage Detection Incomplete" placement="right">
            <CircleInfoIcon class="pr-2 text-je-warning" />
          </el-tooltip>
        </template>
      </div>
    </template>
    <div class="relative h-full w-full defects-tables overflow-y-scroll">
      <template v-if="suggestionsAnnotations.length === 0">
        <div class="h-full w-full pb-4 flex items-center justify-center text-slate-300 text-base font-medium">
          No suggested discrepancies exist
        </div>
      </template>
      <template v-else>
        <table class="defects-table table-fixed w-full text-[14px] text-left">
          <thead>
            <tr class="bg-gray-200">
              <th class="w-[50px]"></th>
              <th>TYPE</th>
              <th>CONFIDENCE</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, i) in groupedSuggestionsArray" :key="i">
              <template v-if="Array.isArray(row)">
                <CollapsibleTableRow
                  :id="`G-${row[0].annotationGroup}`"
                  class="hover:cursor-pointer"
                  :class="getCollapsedHeaderClass(row[0])"
                  @click="handleGroupRowClick(row[0].annotationGroup, true)"
                  @mouseenter="
                    (e) => {
                      handleMouseEnterGroupRow(e, row[0].annotationGroup, true)
                    }
                  "
                  @mouseleave="
                    (e) => {
                      handleMouseLeaveGroupRow(e, row[0].annotationGroup, true)
                    }
                  "
                >
                  <template #main>
                    <td class="border border-zinc-400">{{ DefectTypes[row[0].defectType] }}</td>
                    <td class="border border-zinc-400">
                      {{ getGroupAverageConfidence(row) }}
                    </td>
                  </template>
                  <template #details>
                    <tr :class="getCollapsedRowClass(row[0])">
                      <td colspan="3" class="no-padding">
                        <table class="w-full text-[14px] text-left">
                          <tbody>
                            <template v-for="(_row, _i) in row" :key="_i">
                              <tr
                                :id="_row.annotationUuid"
                                :class="getNestedRowClass(_row)"
                                class="hover:bg-orange-100 hover:cursor-pointer border-l-8 border-solid"
                                @click.stop="handleRowClick(_row)"
                                @mouseenter="(e) => handleMouseEnterRow(e, _row)"
                                @mouseleave="(e) => handleMouseLeaveRow(e, _row)"
                              >
                                <td class="border border-zinc-400 w-[50px] text-center">
                                  <span class="text-amber-500 hover:text-sky-400">
                                    <LightbulbIcon />
                                  </span>
                                </td>
                                <td class="border border-zinc-400">{{ DefectTypes[_row.defectType] }}</td>
                                <td class="border border-zinc-400">
                                  {{ formatConfidence(_row.confidence) }}
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </template>
                </CollapsibleTableRow>
              </template>
              <template v-else>
                <tr
                  :id="row.annotationUuid"
                  class="bg-neutral-200 hover:bg-orange-100 hover:cursor-pointer"
                  :class="getRowClass(row)"
                  @click="handleRowClick(row)"
                  @mouseenter="(e) => handleMouseEnterRow(e, row)"
                  @mouseleave="(e) => handleMouseLeaveRow(e, row)"
                >
                  <td class="border border-zinc-400 w-[50px] text-center p">
                    <span class="text-amber-500 hover:text-sky-400">
                      <LightbulbIcon />
                    </span>
                  </td>
                  <td class="border border-zinc-400">{{ DefectTypes[row.defectType] }}</td>
                  <td class="border border-zinc-400">
                    {{ formatConfidence(row.confidence) }}
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </template>
    </div>
  </el-collapse-item>
</template>

<style>
.defects-table td,
.defects-table th {
  padding: 12px 8px;
}

.defects-table td.no-padding {
  padding: 0;
}
</style>
